import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiError, DAY_LABELS, DAY_ORDER } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function WorkingHours() {
  const { data, isLoading } = useQuery({ queryKey: ["business"], queryFn: () => api.get("/business").then((r) => r.data) });
  const { data: staffList } = useQuery({ queryKey: ["staff"], queryFn: () => api.get("/staff").then((r) => r.data) });
  const activeStaff = (staffList || []).filter((s) => s.active);
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setHours(data.working_hours); }, [data]);

  const update = (day, field, value) => setHours((h) => ({ ...h, [day]: { ...h[day], [field]: value } }));
  const addBreak = (day) => setHours((h) => ({ ...h, [day]: { ...h[day], breaks: [...(h[day].breaks || []), { start: "12:00", end: "13:00" }] } }));
  const updateBreak = (day, idx, field, value) => setHours((h) => { const br = [...(h[day].breaks || [])]; br[idx] = { ...br[idx], [field]: value }; return { ...h, [day]: { ...h[day], breaks: br } }; });
  const removeBreak = (day, idx) => setHours((h) => ({ ...h, [day]: { ...h[day], breaks: (h[day].breaks || []).filter((_, i) => i !== idx) } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/working-hours", { working_hours: hours });
      toast.success("Çalışma saatleri kaydedildi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (isLoading || !hours)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="hours-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Çalışma Saatleri</h1>
          <p className="text-muted-foreground mt-1">Rezervasyon saatleri bu kurallara göre oluşturulur</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-hours-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {DAY_ORDER.map((day) => {
          const d = hours[day];
          return (
            <div key={day} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`hours-row-${day}`}>
              <div className="w-32 font-semibold">{DAY_LABELS[day]}</div>
              <div className="flex items-center gap-2">
                <Switch checked={!d.closed} onCheckedChange={(v) => update(day, "closed", !v)} data-testid={`hours-open-${day}`} />
                <span className="text-sm text-muted-foreground w-16">{d.closed ? "Kapalı" : "Açık"}</span>
              </div>
              {!d.closed && (
                <div className="sm:ml-auto space-y-2">
                  <div className="flex items-center gap-2">
                    <Input type="time" value={d.open} onChange={(e) => update(day, "open", e.target.value)} className="h-10 w-32" data-testid={`hours-from-${day}`} />
                    <span className="text-muted-foreground">-</span>
                    <Input type="time" value={d.close} onChange={(e) => update(day, "close", e.target.value)} className="h-10 w-32" data-testid={`hours-to-${day}`} />
                  </div>
                  {(d.breaks || []).map((br, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">Mola</span>
                      <Input type="time" value={br.start} onChange={(e) => updateBreak(day, idx, "start", e.target.value)} className="h-9 w-28" data-testid={`break-from-${day}-${idx}`} />
                      <span className="text-muted-foreground">-</span>
                      <Input type="time" value={br.end} onChange={(e) => updateBreak(day, idx, "end", e.target.value)} className="h-9 w-28" data-testid={`break-to-${day}-${idx}`} />
                      {activeStaff.length >= 2 && (
                        <select
                          value={br.staff_id || ""}
                          onChange={(e) => updateBreak(day, idx, "staff_id", e.target.value)}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          data-testid={`break-staff-${day}-${idx}`}
                        >
                          <option value="">Tüm personel</option>
                          {activeStaff.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive h-9 px-2" onClick={() => removeBreak(day, idx)} data-testid={`break-remove-${day}-${idx}`}><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => addBreak(day)} data-testid={`break-add-${day}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Kapalı saat aralığı ekle
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
